import React from 'react';
import { Breakpoint } from 'types/utils';
import { jcm } from 'utils';
import { PictureSources } from '../photos-modal.types';
import { thumbsScrollerDesktop } from './modal-layout-desktops.styles';
import { thumbsScroller, thumbsScrollerMobile } from './modal-layout-touch.styles';
import { PictureThumbAtDesktop } from './modal-thumb-at-desktop';
import { PictureThumbAtTouch } from './modal-thumb-at-touch';
import { adjustScrollHorizontal, adjustScrollVertical } from './utils';

export const ModalThumbs: React.FC<{
  showedPicIdx: number;
  photosThumbs: PictureSources[];
  breakpoint: Breakpoint;
  setShowedPicture?: (value: PictureSources | null) => void;
}> = ({ showedPicIdx, photosThumbs, breakpoint, setShowedPicture }) => {
  const activeThumbRef = React.useRef<HTMLButtonElement>(null);
  const thumbsScrollerRef = React.useRef<HTMLDivElement>(null);

  const atDesktop = breakpoint === 'large' || breakpoint === 'medium';
  const PictureThumb = atDesktop ? PictureThumbAtDesktop : PictureThumbAtTouch;

  const thumbs = React.useMemo(
    () =>
      photosThumbs.map((pic, idx) => (
        <PictureThumb
          key={pic.urlOriginal}
          activeThumbRef={activeThumbRef}
          thumbPicture={pic}
          isActive={idx === showedPicIdx}
          setShowedPicture={setShowedPicture}
          breakpoint={breakpoint}
        />
      )),
    [PictureThumb, photosThumbs, activeThumbRef, showedPicIdx, setShowedPicture, breakpoint],
  );

  React.useEffect(() => {
    adjustScroll(atDesktop ? 'vertical' : 'horizontal', thumbsScrollerRef.current, activeThumbRef.current);
  }, [showedPicIdx, atDesktop]);

  return (
    <div
      className={jcm({
        [thumbsScrollerDesktop]: atDesktop,
        [thumbsScroller]: !atDesktop,
        [thumbsScrollerMobile]: !atDesktop && breakpoint === 'mobile',
      })}
      ref={thumbsScrollerRef}
      role="tablist"
    >
      {thumbs}
    </div>
  );
};

export const adjustScroll = (
  strategy: 'vertical' | 'horizontal',
  scrollContainer: HTMLElement | null,
  itemElement: HTMLElement | null,
) => {
  if (scrollContainer instanceof HTMLElement && itemElement instanceof HTMLElement) {
    const containerRect = scrollContainer.getBoundingClientRect();
    const itemRect = itemElement.getBoundingClientRect();

    if (strategy === 'vertical') {
      const containerScrollTop = scrollContainer.scrollTop;
      adjustScrollVertical(containerRect, itemRect, containerScrollTop, (scrollToOptions) =>
        scrollContainer.scrollTo(scrollToOptions),
      );
    } else {
      const containerScrollLeft = scrollContainer.scrollLeft;
      adjustScrollHorizontal(containerRect, itemRect, containerScrollLeft, (scrollToOptions) =>
        scrollContainer.scrollTo(scrollToOptions),
      );
    }
  }
};
